import { createSlice } from "@reduxjs/toolkit";

const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

const getConfiguredTestEmails = () =>
  String(process.env.REACT_APP_TEST_USER_EMAILS || process.env.REACT_APP_TEST_USER_EMAIL || "")
    .split(",")
    .map(normalizeEmail)
    .filter(Boolean);

const isConfiguredTestUser = (email) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) return false;

  return getConfiguredTestEmails().includes(normalizedEmail);
};

const initialState = {
  isAuthenticated: false,
  user: null,
  role: typeof sessionStorage !== "undefined" ? sessionStorage.getItem("userRole") : null,
  token: null,
  isTestUser: false,
  loading: false,
  iStoreId:
    typeof sessionStorage !== "undefined" && sessionStorage.getItem("iStoreId")
      ? Number(sessionStorage.getItem("iStoreId"))
      : null,
  storeName: typeof sessionStorage !== "undefined" ? sessionStorage.getItem("storeName") : null,
};

const AuthSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    loginSuccess: (state, action) => {
      const email = action.payload.user?.email || action.payload.email;
      const isTestUser =
        action.payload.isTestUser ??
        action.payload.user?.isTestUser ??
        isConfiguredTestUser(email);

      state.isAuthenticated = true;
      state.user = action.payload.user || { email };
      state.role = action.payload.role || action.payload.sRole || "user";
      state.token = action.payload.token || action.payload.accessToken;
      state.isTestUser = Boolean(isTestUser);
      state.loading = false;
      state.iStoreId = action.payload.iStoreId ?? action.payload.storeId ?? null;
      state.storeName = action.payload.storeName ?? action.payload.sStoreName ?? null;

      const tokenToSave = action.payload.token || action.payload.accessToken;
      if (tokenToSave) {
        sessionStorage.setItem("accessToken", tokenToSave);
      }
      if (state.role) {
        sessionStorage.setItem("userRole", state.role);
      }
      if (state.iStoreId !== null && state.iStoreId !== undefined) {
        sessionStorage.setItem("iStoreId", String(state.iStoreId));
      }
      if (state.storeName) {
        sessionStorage.setItem("storeName", state.storeName);
      }

      console.log("🔓 [AuthSlice] Auth Redux State:", {
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        role: state.role,
        token: state.token,
        isTestUser: state.isTestUser,
        iStoreId: state.iStoreId,
        storeName: state.storeName,
      });
    },
    loginFailure: (state) => {
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("storeName");
      sessionStorage.removeItem("iStoreId");
      sessionStorage.removeItem("userRole");
      state.isAuthenticated = false;
      state.user = null;
      state.role = null;
      state.token = null;
      state.isTestUser = false;
      state.loading = false;
      state.iStoreId = null;
      state.storeName = null;
    },
    logout: (state) => {
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("storeName");
      sessionStorage.removeItem("iStoreId");
      sessionStorage.removeItem("userRole");
      state.isAuthenticated = false;
      state.user = null;
      state.role = null;
      state.token = null;
      state.isTestUser = false;
      state.loading = false;
      state.iStoreId = null;
      state.storeName = null;
      console.log("🔒 [AuthSlice] Auth Redux State cleared on logout.");
    },
    saveUserToken: (state, action) => {
      state.token = action.payload.token;
      if (action.payload.token) {
        sessionStorage.setItem("accessToken", action.payload.token);
      }
    },
    setStoreId: (state, action) => {
      state.iStoreId = action.payload;
      if (action.payload) {
        sessionStorage.setItem("iStoreId", String(action.payload));
      } else {
        sessionStorage.removeItem("iStoreId");
      }
      console.log("🏪 [AuthSlice] Updated iStoreId:", state.iStoreId);
    },
    setStoreName: (state, action) => {
      state.storeName = action.payload;
      if (action.payload) {
        sessionStorage.setItem("storeName", action.payload);
      } else {
        sessionStorage.removeItem("storeName");
      }
      console.log("🏪 [AuthSlice] Updated storeName:", state.storeName);
    },
    resetData: (state) => {
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("storeName");
      sessionStorage.removeItem("iStoreId");
      sessionStorage.removeItem("userRole");
      state.isAuthenticated = false;
      state.user = null;
      state.role = null;
      state.token = null;
      state.isTestUser = false;
      state.loading = false;
      state.iStoreId = null;
      state.storeName = null;
    },
  },
});

export const { setLoading, loginSuccess, loginFailure, logout, saveUserToken, setStoreId, setStoreName, resetData } =
  AuthSlice.actions;

export default AuthSlice.reducer;
